import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRightIcon,
  CalendarIcon,
  DocumentTextIcon,
  NewspaperIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import {
  PageCta,
  PageHero,
  SectionHeading,
} from '../components/page-templates/SitePageTemplates';

/* The blog index lists the real posts from the inventory app's blog API
 * (/api/blog/posts — the 30 articles migrated from the WordPress site). It used
 * to show three hard-coded cards whose slugs did not exist, which left every
 * real article without a single internal link. */

interface BlogPostSummary {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  featured_image: string | null;
  author: string | null;
  published_at: string | null;
  view_count: number;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const CATEGORY_NEWS = 'Company news';
const CATEGORY_INSIGHT = 'Industry insights';
const NEWS_PATTERN = /\b(expands?|expansion|launch(?:es)?|new coverage|new country|coverage alert)\b/i;

const categoryOf = (post: BlogPostSummary): string => (NEWS_PATTERN.test(post.title) ? CATEGORY_NEWS : CATEGORY_INSIGHT);

const formatMonth = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : '';

const Blog: React.FC = () => {
  const [posts, setPosts] = useState<BlogPostSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const load = async () => {
      try {
        const collected: BlogPostSummary[] = [];
        let page = 1;
        for (;;) {
          const response = await fetch(`${API_BASE_URL}/api/blog/posts?per_page=100&page=${page}`, {
            signal: controller.signal,
          });
          const data = await response.json();
          if (!data.success) throw new Error(data.error || 'Unable to load articles');
          collected.push(...(data.posts as BlogPostSummary[]));
          if (!data.total_pages || page >= data.total_pages) break;
          page += 1;
        }
        if (active) setPosts(collected);
      } catch (err) {
        if (!active) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError('Articles are temporarily unavailable. Please try again in a moment.');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  const featuredPost = posts[0];
  const remainingPosts = posts.slice(1);
  const categories = useMemo(() => {
    const set = new Set(posts.map(categoryOf));
    return [CATEGORY_INSIGHT, CATEGORY_NEWS].filter((c) => set.has(c));
  }, [posts]);

  return (
    <div className="bg-white">
      <PageHero
        eyebrow="Blog"
        title="Working notes on global ICT logistics."
        highlight="global ICT logistics."
        description="The blog covers market entry, customs compliance, freight control, and TrueLog's expansion into new markets, written for teams running hardware programmes across borders."
        heroIcon={DocumentTextIcon}
        accentIcon={ShieldCheckIcon}
        actions={[
          { href: featuredPost ? `/blog/${featuredPost.slug}` : '/blog', label: 'Read the latest article' },
          { href: '/case-studies', label: 'View case studies', variant: 'secondary' },
        ]}
        badges={categories.length ? categories : [CATEGORY_INSIGHT, CATEGORY_NEWS]}
        metrics={[
          { value: posts.length ? `${posts.length}` : '30', label: 'Published articles' },
          { value: '2', label: 'Editorial themes' },
          { value: '140+', label: 'Countries in operating scope' },
        ]}
        panelEyebrow="Latest article"
        panelTitle={featuredPost ? featuredPost.title : loading ? 'Loading the latest article…' : 'New articles coming soon'}
        panelDescription={featuredPost ? featuredPost.excerpt : ''}
        panelItems={
          featuredPost
            ? [
                {
                  icon: categoryOf(featuredPost) === CATEGORY_NEWS ? NewspaperIcon : ShieldCheckIcon,
                  title: categoryOf(featuredPost),
                  description: formatMonth(featuredPost.published_at),
                },
              ]
            : []
        }
      />

      {error && (
        <section className="bg-white py-20">
          <div className="container-custom">
            <div className="rounded-[30px] border border-amber-200 bg-amber-50 p-7 text-sm leading-7 text-amber-800">{error}</div>
          </div>
        </section>
      )}

      {!loading && !error && posts.length === 0 && (
        <section className="bg-white py-20">
          <div className="container-custom">
            <div className="rounded-[30px] border border-slate-200/80 bg-slate-50 p-7 text-sm leading-7 text-slate-600">No articles have been published yet. Check back soon.</div>
          </div>
        </section>
      )}

      {loading && !error && (
        <section className="bg-white py-20">
          <div className="container-custom animate-pulse">
            <div className="h-64 rounded-[34px] bg-slate-100" />
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              <div className="h-56 rounded-[30px] bg-slate-100" />
              <div className="h-56 rounded-[30px] bg-slate-100" />
            </div>
          </div>
        </section>
      )}

      {featuredPost && (
        <section className="bg-white py-20">
          <div className="container-custom">
            <SectionHeading
              eyebrow="Latest"
              title="Start with the most recent article"
              description="The newest piece first; the archive below runs back to our earliest notes on compliance, freight and market entry."
            />

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.75 }}
              className="rounded-[34px] border border-slate-200/80 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 p-7 text-white shadow-[0_30px_80px_-48px_rgba(15,23,42,0.88)] sm:p-9"
            >
              <span className="rounded-full bg-white/[0.08] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-300">
                {categoryOf(featuredPost)}
              </span>
              <h2 className="mt-5 max-w-3xl text-3xl font-bold tracking-tight text-white sm:text-4xl">{featuredPost.title}</h2>
              <p className="mt-4 max-w-3xl text-sm leading-8 text-slate-300 sm:text-base">{featuredPost.excerpt}</p>
              <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-slate-300">
                <span className="inline-flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  {formatMonth(featuredPost.published_at)}
                </span>
              </div>
              <Link
                to={`/blog/${featuredPost.slug}`}
                className="mt-8 inline-flex items-center gap-2 rounded-2xl bg-[#0E9ED5] px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-[#0b7aa6]"
              >
                Read article
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {remainingPosts.length > 0 && (
        <section className="bg-slate-50 py-20">
          <div className="container-custom">
            <SectionHeading
              eyebrow="Archive"
              title="More articles from the operating layer"
              description="These posts focus on the practical mechanics behind global ICT execution, not generic supply chain commentary."
            />

            <div className="grid gap-6 md:grid-cols-2">
              {remainingPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: Math.min(index, 6) * 0.06 }}
                  className="rounded-[30px] border border-slate-200/80 bg-white p-7 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.28)]"
                >
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                    {categoryOf(post)}
                  </span>
                  <h2 className="mt-5 text-2xl font-semibold text-slate-950">
                    <Link to={`/blog/${post.slug}`} className="transition-colors hover:text-[#2447d8]">
                      {post.title}
                    </Link>
                  </h2>
                  <p className="mt-3 text-sm leading-7 text-slate-600">{post.excerpt}</p>
                  <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-slate-500">
                    <span className="inline-flex items-center gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      {formatMonth(post.published_at)}
                    </span>
                  </div>
                  <Link
                    to={`/blog/${post.slug}`}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#2447d8] transition-colors hover:text-[#0E9ED5]"
                  >
                    Read article
                    <ArrowRightIcon className="h-4 w-4" />
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      <PageCta
        title="Need insight tied to your own rollout or market-entry plan?"
        description="If the articles are directionally useful but your environment has different countries, timelines, or compliance constraints, we can work through those specifics with you."
        pills={[CATEGORY_INSIGHT, CATEGORY_NEWS]}
        secondaryHref="/resources"
        secondaryLabel="Explore resources"
      />
    </div>
  );
};

export default Blog;
